/**
 * PublicRecordDrawer — shows the SF public record for a parcel.
 *
 * A real estate developer needs the public picture alongside the listing:
 * assessed value + tax basis, zoning (what can I build), permit history, and
 * open code complaints / violations. Fetches GET /properties/:blockLot/
 * public-record and renders the assembled sections. Degrades gracefully when a
 * dataset hasn't been ingested yet (shows an "unavailable" note, not an error).
 */

import { useEffect, useState } from "react";
import { useAINative } from "../../providers/AINativeProvider.js";

// ─── Response types (mirror apps/api PublicRecordService) ───────────────────────

interface ParcelRow {
  zoning_code?: string;
  zoning_district?: string;
  address?: string;
  neighborhood?: string;
  [k: string]: unknown;
}
interface AssessmentRow {
  assessed_land_value?: number | null;
  assessed_improvement_value?: number | null;
  assessed_total_value?: number | null;
  use_definition?: string;
  year_built?: number | null;
  [k: string]: unknown;
}
interface PermitRow {
  entity_id?: string;
  permit_type?: string;
  status?: string;
  description?: string;
  estimated_cost?: number | null;
  is_active?: boolean;
  [k: string]: unknown;
}
interface DistressRow {
  entity_id?: string;
  signal_type?: string;
  category?: string;
  description?: string;
  status?: string;
  is_open?: boolean;
  [k: string]: unknown;
}
export interface PublicRecord {
  block_lot: string;
  parcel: ParcelRow | null;
  assessment: AssessmentRow | null;
  permits: { total: number; active: number; items: PermitRow[] };
  distress: { total: number; open: number; items: DistressRow[] };
  unavailable: string[];
}

type Status = "loading" | "success" | "error";

function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

export interface PublicRecordDrawerProps {
  /** SF block-lot / APN (the deal card id stripped of "property:"). */
  blockLot: string;
  /** Human label for the header (address). */
  title: string;
  onClose: () => void;
}

export function PublicRecordDrawer({ blockLot, title, onClose }: PublicRecordDrawerProps) {
  const { api } = useAINative();
  const [status, setStatus] = useState<Status>("loading");
  const [record, setRecord] = useState<PublicRecord | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError("");
    const load = async () => {
      try {
        const data = await api.call<PublicRecord>(
          `/properties/${encodeURIComponent(blockLot)}/public-record`,
        );
        if (cancelled) return;
        setRecord(data);
        setStatus("success");
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load public record");
        setStatus("error");
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, blockLot]);

  return (
    <div className="pubrec-drawer" role="dialog" aria-modal="true" aria-label={`Public record: ${title}`}>
      <div className="pubrec-drawer__backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="pubrec-drawer__panel">
        <header className="pubrec-drawer__header">
          <div>
            <div className="pubrec-drawer__eyebrow">SF Public Record</div>
            <h2 className="pubrec-drawer__title">{title}</h2>
            <div className="pubrec-drawer__blocklot">Block-lot {blockLot}</div>
          </div>
          <button className="pubrec-drawer__close" onClick={onClose} aria-label="Close public record">
            ×
          </button>
        </header>

        <div className="pubrec-drawer__body">
          {status === "loading" && (
            <div className="pubrec-drawer__loading" role="status">
              Loading public record…
            </div>
          )}

          {status === "error" && (
            <div className="pubrec-drawer__error" role="alert">
              {error}
            </div>
          )}

          {status === "success" && record && (
            <>
              {/* Zoning + assessed value */}
              <section className="pubrec-section" aria-label="Assessment & zoning">
                <h3 className="pubrec-section__title">Tax &amp; Zoning</h3>
                <div className="pubrec-grid">
                  <div className="pubrec-stat">
                    <span className="pubrec-stat__label">Assessed value</span>
                    <span className="pubrec-stat__value">{money(record.assessment?.assessed_total_value)}</span>
                  </div>
                  <div className="pubrec-stat">
                    <span className="pubrec-stat__label">Land / Improvement</span>
                    <span className="pubrec-stat__value">
                      {money(record.assessment?.assessed_land_value)} /{" "}
                      {money(record.assessment?.assessed_improvement_value)}
                    </span>
                  </div>
                  <div className="pubrec-stat">
                    <span className="pubrec-stat__label">Zoning</span>
                    <span className="pubrec-stat__value">
                      {record.parcel?.zoning_code ?? "—"}
                      {record.parcel?.zoning_district ? ` · ${record.parcel.zoning_district}` : ""}
                    </span>
                  </div>
                  <div className="pubrec-stat">
                    <span className="pubrec-stat__label">Use / Year built</span>
                    <span className="pubrec-stat__value">
                      {record.assessment?.use_definition ?? "—"}
                      {record.assessment?.year_built ? ` · ${record.assessment.year_built}` : ""}
                    </span>
                  </div>
                </div>
              </section>

              {/* Permits */}
              <section className="pubrec-section" aria-label="Permit history">
                <h3 className="pubrec-section__title">
                  Permits <span className="pubrec-count">{record.permits.total}</span>
                  {record.permits.active > 0 && (
                    <span className="pubrec-badge pubrec-badge--active">{record.permits.active} active</span>
                  )}
                </h3>
                {record.permits.items.length === 0 ? (
                  <p className="pubrec-empty">No permits on record.</p>
                ) : (
                  <ul className="pubrec-list">
                    {record.permits.items.slice(0, 8).map((p, i) => (
                      <li key={p.entity_id ?? i} className="pubrec-list__item">
                        <span className={`pubrec-dot ${p.is_active ? "pubrec-dot--active" : ""}`} aria-hidden="true" />
                        <span className="pubrec-list__main">{p.permit_type || "Permit"}</span>
                        <span className="pubrec-list__meta">
                          {p.status}
                          {p.estimated_cost ? ` · ${money(p.estimated_cost)}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Distress */}
              <section className="pubrec-section" aria-label="Complaints & violations">
                <h3 className="pubrec-section__title">
                  Complaints &amp; Violations <span className="pubrec-count">{record.distress.total}</span>
                  {record.distress.open > 0 && (
                    <span className="pubrec-badge pubrec-badge--warn">{record.distress.open} open</span>
                  )}
                </h3>
                {record.distress.items.length === 0 ? (
                  <p className="pubrec-empty">No complaints or violations on record.</p>
                ) : (
                  <ul className="pubrec-list">
                    {record.distress.items.slice(0, 8).map((d, i) => (
                      <li key={d.entity_id ?? i} className="pubrec-list__item">
                        <span className={`pubrec-dot ${d.is_open ? "pubrec-dot--warn" : ""}`} aria-hidden="true" />
                        <span className="pubrec-list__main">
                          {d.category || d.signal_type || "Signal"}
                          {d.description ? `: ${d.description}` : ""}
                        </span>
                        <span className="pubrec-list__meta">{d.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {record.unavailable.length > 0 && (
                <p className="pubrec-note" role="note">
                  Some public datasets are not yet loaded for this parcel.
                </p>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
